// Declarative description of the Purchase Order page: which tabs exist, what
// each one queries, which filter fields / columns / row actions it shows.
// Behaviour lives in the components; this file is the spec they read.
import React from 'react';
import moment from 'moment';
import { Badge, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import statuses from './static/statuses';
import { formatRp, monthStart } from './utils';

/** Order here is the order of the tab bar (after permission filtering). */
export const TAB_NAMES = [
  'PO List',
  'PO Data',
  'PO History',
  'PO Approval',
  'PO Pencairan',
  'PO Finalization',
  'Edit Mutation Date',
];

export const DETAIL_TAB = 'PO Data';

// ---- filter fields ---------------------------------------------------------
// Keys are the backend body keys, so the applied filter is spread straight
// into the request body without any mapping.

const withAll = (options) => [{ value: '', label: 'All' }, ...options];

const text = (label) => ({ label, type: 'text' });
const date = (label) => ({ label, type: 'date' });
const expenseTypeSelect = {
  label: 'Tujuan Pengeluaran',
  type: 'select',
  options: ({ expenseTypes }) =>
    withAll(
      expenseTypes.map((t) => ({ value: t.id, label: t.tujuanPengeluaran })),
    ),
};

export const FILTER_FIELDS = {
  poDateFrom: date('Date From'),
  poDateTo: date('Date To'),
  poNumber: text('PO Number'),
  createdBy: text('User'),
  username: text('User'),
  remarks: text('Remarks'),
  branchId: {
    label: 'Branch',
    type: 'select',
    options: ({ branches }) =>
      withAll(branches.map((b) => ({ value: b.id, label: b.name }))),
  },
  tujuanPengeluaran: expenseTypeSelect,
  masterTujuanPengeluaranId: expenseTypeSelect,
  status: { label: 'Status', type: 'select', options: () => statuses },
};

/**
 * The filter a tab starts with: '' for every text/select field it shows,
 * plus a month-to-date range when the tab asks for one. Date fields without
 * a range are left undefined so they are omitted from the request body.
 */
export const defaultFilter = (tab) => {
  const filter = {};
  tab.filterFields.forEach((key) => {
    if (FILTER_FIELDS[key].type !== 'date') filter[key] = '';
  });
  if (tab.dateRange === 'month') {
    filter.poDateFrom = monthStart();
    filter.poDateTo = new Date();
  }
  return filter;
};

// ---- columns ---------------------------------------------------------------
// { header, render(po, ctx), style?, className? }. ctx is built by PoListTab
// and carries { tab, onPreview, ... }.

const badgeCell = ({ text: label, color }, createdBy) => (
  <div>
    <Badge className="mt-1" color={color} pill style={{ fontSize: '14px' }}>
      {label}
    </Badge>
    <p className="main-text mt-2" style={{ fontSize: '12px' }}>
      {createdBy}
    </p>
  </div>
);

const textColumn = (header, field, width) => ({
  header,
  style: width ? { width } : undefined,
  render: (po) => po[field],
});

const dateColumn = (header, field, format, width) => ({
  header,
  style: { width },
  render: (po) => (po[field] ? moment(po[field]).format(format) : 'N/A'),
});

const historyStatus = ({ isProcess, isApproved, isCair, isCancel }) => {
  if (isCancel) return 'Cancelled';
  if (!isProcess && !isApproved && !isCair) return 'Belum Diproses';
  if (isProcess && !isApproved && !isCair) return 'Belum Diapprove';
  if (isProcess && isApproved && !isCair) return 'Belum Dicairkan';
  return 'Sudah Cair';
};

export const COLUMNS = {
  status: {
    header: 'Status',
    className: 'text-center',
    style: { width: '8.56rem' },
    render: (po, { tab }) => badgeCell(tab.badge(po), po.createdBy),
  },
  code: {
    header: 'PO Number',
    className: 'text-center',
    style: { width: '8.56rem' },
    render: (po) =>
      badgeCell(
        { text: po.code, color: po.isCancel ? 'secondary' : 'success' },
        po.createdBy,
      ),
  },
  poNumber: {
    header: 'PO Number',
    className: 'text-center',
    style: { width: '9.375rem' },
    render: (po) => (
      <div>
        <p className="main-text">{po.code}</p>
        <span style={{ fontSize: '12px' }}>
          {moment(po.tglPO).format('DD-MMM-YYYY HH:mm')} WIB
        </span>
      </div>
    ),
  },
  tujuan: textColumn('Tujuan Pengeluaran', 'tujuanPengeluaran', '11.562rem'),
  branch: textColumn('Branch', 'branchName', '9.375rem'),
  periodStart: dateColumn(
    'Period Start',
    'periodStart',
    'DD-MMM-yyy',
    '8.56rem',
  ),
  periodEnd: dateColumn('Period End', 'periodEnd', 'DD-MMM-yyy', '8.56rem'),
  historyPeriodStart: dateColumn(
    'Period Start',
    'periodStart',
    'DD/MM/yyy',
    '10rem',
  ),
  historyPeriodEnd: dateColumn('Period End', 'periodEnd', 'DD/MM/yyy', '10rem'),
  poDate: {
    header: 'Po Date',
    style: { width: '10rem' },
    render: (po) => moment(po.tglPO).format('DD/MM/yyy'),
  },
  totalPrice: {
    header: 'Total Price',
    style: { width: '8.125rem' },
    render: (po) => formatRp(po.totalPrice),
  },
  approvedBy: textColumn('Approved By', 'updatedBy', '8.4375rem'),
  liquidBy: textColumn('Liquid By', 'updatedBy', '8.4375rem'),
  addedBy: textColumn('Added By', 'updatedBy', '8.4375rem'),
  remarks: textColumn('Remarks', 'remarks'),
  latestStatus: { header: 'Latest Status', render: historyStatus },
  proof: {
    header: 'Proof',
    style: { width: '4rem' },
    render: (po, { onPreview }) =>
      po.proof ? (
        <Button size="sm" onClick={() => onPreview(po.proof)}>
          <FontAwesomeIcon icon={['fa', 'image']} />
        </Button>
      ) : (
        <em>N/A</em>
      ),
  },
};

// ---- tabs ------------------------------------------------------------------

const listBadge = ({ isReject, isCancel }) => {
  if (!isReject && !isCancel) return { text: 'Pending', color: 'warning' };
  if (isCancel && !isReject) return { text: 'Cancelled', color: 'secondary' };
  return { text: 'Rejected', color: 'danger' };
};

const fixedBadge = (label, color) => () => ({ text: label, color });

const flags = (isProcess, isCair, isApproved, booleanGlJournal) => ({
  isProcess,
  isCair,
  isApproved,
  booleanGlJournal,
});

const C = COLUMNS;
const LIST_COLUMNS = [
  C.status,
  C.poNumber,
  C.tujuan,
  C.branch,
  C.periodStart,
  C.periodEnd,
  C.totalPrice,
];
const APPROVAL_FILTER = [
  'poDateFrom',
  'poNumber',
  'poDateTo',
  'createdBy',
  'tujuanPengeluaran',
  'remarks',
];
const FINAL_FILTER = ['poNumber', 'createdBy', 'tujuanPengeluaran'];

/**
 * Per list tab:
 *  endpoint/flags   what to POST, together with { page, ...appliedFilter }
 *  filterFields     FILTER_FIELDS keys, in layout order (two per row)
 *  dateRange        'month' → poDateFrom/poDateTo default to month-to-date
 *  filterOpen       filter panel starts expanded
 *  columns          COLUMNS entries; the Action column is always appended
 *  badge(po)        { text, color } for the status column
 *  rowActions       RowActions.ACTIONS keys, top to bottom
 *  detailsMode      PO Data mode the "Details" action opens
 */
export const TABS = {
  'PO List': {
    endpoint: '/poHeader/getAllPoList',
    flags: flags(false, false, false, false),
    filterFields: ['poNumber', 'branchId', 'tujuanPengeluaran', 'status'],
    columns: [...LIST_COLUMNS, C.remarks],
    badge: listBadge,
    rowActions: ['process', 'cancel', 'details', 'print'],
    detailsMode: 'edit',
  },
  'PO Approval': {
    endpoint: '/poHeader/getAllPo',
    flags: flags(true, false, false, false),
    filterFields: APPROVAL_FILTER,
    dateRange: 'month',
    columns: [...LIST_COLUMNS, C.remarks],
    badge: fixedBadge('Process', 'success'),
    rowActions: ['approve', 'reject', 'details', 'print'],
    detailsMode: 'view',
  },
  'PO Pencairan': {
    endpoint: '/poHeader/getAllPo',
    flags: flags(true, false, true, false),
    filterFields: APPROVAL_FILTER,
    columns: [...LIST_COLUMNS, C.approvedBy, C.remarks, C.proof],
    badge: fixedBadge('Approved', 'info'),
    rowActions: ['liquid', 'print', 'details', 'upload'],
    detailsMode: 'view',
  },
  'PO Finalization': {
    endpoint: '/poHeader/getAllPo',
    flags: flags(true, true, true, false),
    filterFields: FINAL_FILTER,
    columns: [...LIST_COLUMNS, C.liquidBy, C.remarks],
    badge: fixedBadge('Liquid', 'primary'),
    rowActions: ['addToGl', 'details'],
    detailsMode: 'viewMutation',
  },
  'Edit Mutation Date': {
    endpoint: '/poHeader/getAllPo',
    flags: flags(true, true, true, true),
    filterFields: FINAL_FILTER,
    columns: [
      C.status,
      C.poNumber,
      C.tujuan,
      C.totalPrice,
      C.addedBy,
      C.remarks,
    ],
    badge: fixedBadge('Added To GL Journal', 'secondary'),
    rowActions: ['editMutation', 'details'],
    detailsMode: 'viewMutation',
  },
  'PO History': {
    endpoint: '/poHeader/getPoHistory',
    flags: {},
    filterFields: [
      'poDateFrom',
      'poNumber',
      'poDateTo',
      'username',
      'masterTujuanPengeluaranId',
      'remarks',
      'branchId',
    ],
    dateRange: 'month',
    filterOpen: true,
    columns: [
      C.code,
      C.tujuan,
      C.branch,
      C.historyPeriodStart,
      C.historyPeriodEnd,
      C.poDate,
      C.totalPrice,
      C.latestStatus,
      C.proof,
    ],
    rowActions: ['details', 'print'],
    detailsMode: 'view',
  },
};

Object.keys(TABS).forEach((name) => {
  TABS[name] = { ...TABS[name], name };
});

// ---- PO Data modes ---------------------------------------------------------
// A list row opens the PO Data tab in one of these modes; `create` is the
// blank form. Each mode says what the tab shows/enables and which action
// button DetailActions renders.

const bankRow = (hydrateBank, bankDisabled, mutationDisabled) => ({
  showBank: true,
  hydrateBank,
  bankDisabled,
  mutationDisabled,
});

export const MODES = {
  create: {
    readOnly: false,
    showItems: false,
    showBank: false,
    action: 'save',
  },
  edit: { readOnly: false, showItems: true, showBank: false, action: 'update' },
  view: { readOnly: true, showItems: true, showBank: false, action: null },
  liquid: {
    readOnly: true,
    showItems: true,
    ...bankRow(false, false, false),
    action: 'liquid',
  },
  addToGl: {
    readOnly: true,
    showItems: true,
    ...bankRow(true, false, false),
    action: 'addToGl',
  },
  viewMutation: {
    readOnly: true,
    showItems: true,
    ...bankRow(true, true, true),
    action: null,
  },
  editMutation: {
    readOnly: true,
    showItems: true,
    ...bankRow(true, true, false),
    action: 'editMutation',
  },
};
